import type { IStockChangedEvent } from '@/domain/catalog/catalog-events';
import type {
  IAssistantAffectedGroup,
  IAssistantApplyResult,
  IAssistantRow,
  IAssistantStaleDetails,
  IAssistantSummary,
  TAssistantKind,
} from '@/interfaces/assistant.interface';
import type {
  IGhAction,
  IGhCalloutCard,
  IGhCalloutRow,
  IGhNavigateTarget,
  IGhPayload,
} from '../gpthelp-host-contract';
import {
  cardTextLength,
  clampText,
  fitText,
  joinSentences,
  sentenceRoom,
} from './card-text';
import type { ICardFormat } from './card-text';
import { affectedGroupByRow, grownGroupText } from './affected-cards';
import { changesInventory, ofKind } from './card-kinds';
import {
  discardAction,
  refreshAction,
  stillHiddenSentence,
} from './proposal-cards';
import type { IProposalCardInput } from './proposal-cards';
import { appliedTitle } from './result-titles';
import {
  isOptionGroupRow,
  isProductRow,
  presenterOf,
  presenterOfValue,
  rowLabelWithField,
  sizesProductName,
} from './row-presenters';
import {
  addsOtherThanProducts,
  addsProducts,
  isProductKey,
  isStockItemKey,
  productCount,
  rowItemLabel,
  singleProductRow,
  summaryAddsOtherThanProducts,
} from './row-values';

/**
 * Result cards of the card actions (plan §9.9). Each one replaces the card
 * whose button was tapped, so it keeps that card's id.
 */

/** Host contract §3.3 caps of a callout. */
const TITLE_MAX = 160;
const TEXT_MAX = 600;
const LABEL_MAX = 40;
const HISTORY_LABEL_MAX = 24;
const ROW_LABEL_MAX = 160;
const ROW_VALUE_MAX = 60;
const CALLOUT_ROWS_MAX = 8;
/** Products «Ver en el menú» flashes at most (navigate target cap). */
const HIGHLIGHT_MAX = 200;

function notice(
  id: string,
  tone: IGhCalloutCard['tone'],
  title: string
): IGhCalloutCard {
  return {
    id,
    v: 1,
    type: 'callout',
    tone,
    title: clampText(title, TITLE_MAX),
  };
}

/** «11 cambios»: the conversation's history badge; null clears it at 0. */
function historyLabelOf(count: number, fmt: ICardFormat): string | null {
  return count > 0
    ? clampText(fmt.tc('assistant.history.changes', count), HISTORY_LABEL_MAX)
    : null;
}

export interface IAppliedCardInput {
  id: string;
  result: IAssistantApplyResult;
  /** The proposal's rows (to name the products); null when they could not be read. */
  rows: IAssistantRow[] | null;
  /** How many rows the proposal had, for when `rows` is unknown. */
  rowCount: number;
  /**
   * By row key of `rows`: what still hides from customers here something the
   * row turned on (its category, menu or hours; plan P0-3), as the proposal
   * card said. Rows without reasons are absent.
   */
  stillHidden: IProposalCardInput['stillHidden'];
  restaurantId: number;
  /** The store name, for the undo's store check («Este cambio es de …»). */
  storeName: string;
  /** The Menú page this member can open («Ver en el menú»); null: no button. */
  menuPage: string | null;
  /**
   * What the change was of (plan P5-3): an inventory change has its own
   * title and offers the stock page instead of the Menú.
   */
  kind: TAssistantKind;
  /** The stock page this member can open («Ver el stock»); null: no button. */
  stockPage: string | null;
  /**
   * The proposal's branch (whose inventory an inventory change changed);
   * null when unknown.
   */
  branchId: number | null;
}

/**
 * «3 productos quedaron como estaban.», or «3 cambios no se aplicaron.» for
 * rows that are not all products' (plan P2-4); nothing for fewer than two.
 */
function keptManySentence(
  count: number,
  products: boolean,
  fmt: ICardFormat
): string {
  if (count < 2) return '';
  return fmt.t(
    products ? 'assistant.result.keptMany' : 'assistant.result.keptManyAny',
    { n: fmt.number(count) }
  );
}

/**
 * «Pomelo 1,5 L quedó como estaba.» / «3 productos quedaron como estaban.»,
 * counted in products (both sizes of a pizza are one); rows that are not all
 * products' are counted as changes. Without the rows, only a count of the
 * rows left out, never a wrong name.
 */
function keptSentence(
  keptRows: IAssistantRow[] | null,
  keptRowCount: number,
  fmt: ICardFormat
): string {
  if (!keptRows) return keptManySentence(keptRowCount, true, fmt);
  const product = singleProductRow(keptRows);
  if (!product)
    return keptRows.every(isProductRow)
      ? keptManySentence(productCount(keptRows), true, fmt)
      : keptManySentence(keptRows.length, false, fmt);
  return fitText(
    fmt,
    'assistant.result.keptOne',
    {
      name: keptRows.length === 1 ? rowItemLabel(product) : product.itemName,
    },
    'name',
    TITLE_MAX
  );
}

/**
 * «Brownie sigue sin verse: su categoría está oculta.» when an applied row
 * turned on something its category, menu or hours still hide, so the
 * receipt never reads as if customers now see it; fits `room` or is left
 * out. Nothing when no applied row did.
 */
function receiptStillHiddenSentence(
  appliedRows: IAssistantRow[],
  stillHidden: IAppliedCardInput['stillHidden'],
  fmt: ICardFormat,
  room: number
): string {
  const hiddenRows = appliedRows.filter((row) => stillHidden[row.key]?.length);
  if (!hiddenRows.length) return '';
  const sentence = stillHiddenSentence(hiddenRows, stillHidden, fmt, room);
  return cardTextLength(sentence) <= room ? sentence : '';
}

function viewMenuTarget(
  result: IAssistantApplyResult,
  menuPage: string
): IGhNavigateTarget {
  const target: IGhNavigateTarget = { page: menuPage };
  if (result.categoryIds.length)
    target.params = { categoryId: String(result.categoryIds[0]) };
  if (result.itemIds.length)
    target.highlight = result.itemIds.slice(0, HIGHLIGHT_MAX).map(String);
  return target;
}

/**
 * Whether an apply changed option groups themselves (a new group, its rules,
 * texts or choices): the groups the Action API names (`optionGroupIds`, the
 * created and edited ones), or the applied rows when they were read. The
 * pages that list groups then reload (`OPTION_GROUPS_CHANGED_EVENT`), also
 * after its undo.
 */
export function appliedChangesOptionGroups(
  result: Pick<IAssistantApplyResult, 'optionGroupIds'>,
  appliedRows: readonly IAssistantRow[]
): boolean {
  return (
    (result.optionGroupIds ?? []).length > 0 ||
    appliedRows.some(isOptionGroupRow)
  );
}

/**
 * Summaries whose rows may replace a recipe (plan P5-1): a recipe op, or a
 * mix of ops.
 */
const RECIPE_CODES: ReadonlyArray<IAssistantSummary['code']> = [
  'setRecipe',
  'mixed',
];

/**
 * The products whose recipe, or a variant's, the rows replace (a choice's
 * recipe has none).
 */
function recipeProductIds(rows: readonly IAssistantRow[]): number[] {
  const ids = rows.flatMap((row) =>
    row.field === 'recipe' && row.itemId !== null ? [row.itemId] : []
  );
  return [...new Set(ids)].sort((a, b) => a - b);
}

/**
 * What an inventory apply changed (plan P5-3), for the pages that show the
 * inventory (`STOCK_CHANGED_EVENT`): the stock items the Action API names,
 * and the products whose recipe the applied rows replaced. Without the rows
 * (`appliedRows` null: not read), a change that may hold a recipe names no
 * product, so any may have changed. Null for a change of the menu.
 */
export function appliedStockChange(
  result: Pick<IAssistantApplyResult, 'stockItemIds' | 'summary'>,
  input: {
    kind: TAssistantKind;
    appliedRows: readonly IAssistantRow[] | null;
    restaurantId: number;
    branchId: number | null;
  }
): IStockChangedEvent | null {
  if (!changesInventory(input.kind)) return null;
  let itemIds: number[] | null = [];
  if (input.appliedRows) itemIds = recipeProductIds(input.appliedRows);
  else if (RECIPE_CODES.includes(result.summary.code)) itemIds = null;
  return {
    restaurantId: input.restaurantId,
    branchId: input.branchId,
    itemIds,
    stockItemIds: [...(result.stockItemIds ?? [])],
  };
}

/**
 * «Ver en el menú» for a change of the menu; none when the member cannot
 * open it.
 */
function viewMenuAction(
  input: IAppliedCardInput,
  fmt: ICardFormat
): IGhAction | null {
  return input.menuPage
    ? {
        id: 'viewMenu',
        kind: 'navigate',
        label: clampText(fmt.t('assistant.result.viewMenu'), LABEL_MAX),
        icon: 'eye',
        style: 'outline',
        target: viewMenuTarget(input.result, input.menuPage),
      }
    : null;
}

/**
 * «Ver el stock» for an inventory change (plan P5-3: its rows are not on
 * the Menú); none when the member cannot open the stock page.
 */
function viewStockAction(
  input: IAppliedCardInput,
  fmt: ICardFormat
): IGhAction | null {
  return input.stockPage
    ? {
        id: 'viewStock',
        kind: 'navigate',
        label: clampText(fmt.t('assistant.result.viewStock'), LABEL_MAX),
        icon: 'eye',
        style: 'outline',
        target: { page: input.stockPage },
      }
    : null;
}

/** The receipt's page button of each kind. */
const VIEW_ACTIONS: Readonly<
  Record<
    TAssistantKind,
    (input: IAppliedCardInput, fmt: ICardFormat) => IGhAction | null
  >
> = {
  'menu.changes': viewMenuAction,
  'stock.changes': viewStockAction,
};

/**
 * «Aplicado · Se cambiaron 11 precios en Bebidas.»: what still hides
 * something it turned on, until when it can be undone and what stayed as it
 * was, with «Ver en el menú» (or, for an inventory change, «Ver el stock»)
 * and «Deshacer» (the receipt). Titles count the applied rows only.
 */
export function appliedCard(
  input: IAppliedCardInput,
  fmt: ICardFormat
): IGhCalloutCard {
  const { result } = input;
  const applied = new Set(result.appliedKeys);
  const appliedRows = (input.rows ?? []).filter((row) => applied.has(row.key));
  const keptRows = input.rows
    ? input.rows.filter((row) => !applied.has(row.key))
    : null;
  const keptRowCount = Math.max(0, input.rowCount - result.appliedCount);
  const title = clampText(
    appliedTitle(
      result.summary,
      result.appliedCount,
      singleProductRow(appliedRows)?.itemName ?? null,
      fmt,
      sizesProductName(appliedRows),
      input.kind
    ),
    TITLE_MAX
  );

  // The rows are read only when the receipt needs them to name or count
  // something: without them, the summary of a change made only of
  // creations tells it (a choice added to a group, live run L-P3-2).
  const createdAny =
    (result.createdCategoryIds ?? []).length > 0 ||
    addsOtherThanProducts(appliedRows) ||
    summaryAddsOtherThanProducts(result.summary);
  const actions: IGhAction[] = [];
  const view = ofKind(VIEW_ACTIONS, input.kind)(input, fmt);
  if (view) actions.push(view);
  actions.push({
    id: 'undo',
    kind: 'host',
    label: clampText(fmt.t('assistant.result.undo'), LABEL_MAX),
    icon: 'undo',
    style: 'ghost',
    pendingLabel: clampText(fmt.t('assistant.result.undoing'), LABEL_MAX),
    // Undo is store-wide: no branch, so only a store switch refuses it here.
    // `created`: it added products (new ones or copies), whatever its summary;
    // `createdAny`: it added something else (a category, an option group or
    // a choice); `optionGroups`: it changed option groups themselves, so the
    // pages that list them reload after the undo too; `stock`: it changed
    // the inventory (of `stockBranchId`), so the pages that show it follow
    // the undo (plan P5-3).
    payload: {
      actionId: result.proposalId,
      restaurantId: input.restaurantId,
      scopeName: input.storeName,
      created: result.createdItemIds.length > 0 || addsProducts(appliedRows),
      ...(createdAny ? { createdAny } : {}),
      ...(appliedChangesOptionGroups(result, appliedRows)
        ? { optionGroups: true }
        : {}),
      ...(changesInventory(input.kind)
        ? {
            stock: true,
            ...(input.branchId !== null
              ? { stockBranchId: input.branchId }
              : {}),
          }
        : {}),
      title,
    },
  });

  const undoAndKept = [
    fmt.t('assistant.result.undoUntil', {
      date: fmt.date(result.undoUntil),
    }),
    keptSentence(keptRows, keptRowCount, fmt),
  ];
  const stillHidden = receiptStillHiddenSentence(
    appliedRows,
    input.stillHidden,
    fmt,
    sentenceRoom(fmt.locale, undoAndKept, TEXT_MAX)
  );

  return {
    id: input.id,
    v: 1,
    type: 'callout',
    tone: 'success',
    badge: {
      text: clampText(fmt.t('assistant.result.applied'), LABEL_MAX),
      tone: 'success',
      icon: 'check',
    },
    title,
    text: clampText(
      joinSentences(fmt.locale, [stillHidden, ...undoAndKept]),
      TEXT_MAX
    ),
    actions,
    historyLabel: historyLabelOf(result.conversationAppliedCount, fmt),
  };
}

export interface IUndoneCardInput {
  id: string;
  /** Rows kept because someone changed them after the change. */
  kept: number;
  /**
   * The keys of the kept rows (`IAssistantUndoResult.keptRows`); absent:
   * products only (an update from photos).
   */
  keptKeys?: readonly string[];
  /** The change added products: they were removed. */
  created: boolean;
  /**
   * The change added something other than products (a category, an option
   * group or a choice): what was added was removed. Absent: nothing else (an
   * update from photos).
   */
  createdAny?: boolean;
  /**
   * The conversation's applied changes after the undo, for its history
   * badge (`IAssistantUndoResult.conversationAppliedCount`); omitted outside
   * the conversation that made the change.
   */
  conversationAppliedCount?: number;
}

/**
 * «1 producto se modificó después y quedó como está.», or «1 cambio no se
 * deshizo: se modificó después.» when a kept row is not a product's (a
 * category, a menu's order; plan P2-4).
 */
export function undoKeptText(
  kept: number,
  keptKeys: readonly string[],
  fmt: ICardFormat
): string {
  return fmt.tc(
    keptKeys.every(isProductKey)
      ? 'assistant.result.kept'
      : 'assistant.result.keptAny',
    kept
  );
}

/**
 * «Deshecho. Volvieron los valores anteriores.»; «…Se quitaron los productos
 * agregados.» when it added products only, «…Se quitó lo que se agregó.»
 * when it added something else too (a category, an option group or a choice).
 */
function undoneTitleKey(input: IUndoneCardInput): string {
  if (input.createdAny) return 'assistant.result.undoneCreatedAny';
  return input.created
    ? 'assistant.result.undoneCreated'
    : 'assistant.result.undone';
}

/** «Deshecho. Volvieron los valores anteriores.», and what was kept. */
export function undoneCard(
  input: IUndoneCardInput,
  fmt: ICardFormat
): IGhCalloutCard {
  const card = notice(input.id, 'neutral', fmt.t(undoneTitleKey(input)));
  if (input.kept > 0)
    card.text = clampText(
      undoKeptText(input.kept, input.keptKeys ?? [], fmt),
      TEXT_MAX
    );
  if (input.conversationAppliedCount !== undefined)
    card.historyLabel = historyLabelOf(input.conversationAppliedCount, fmt);
  return card;
}

type TStaleEntry = IAssistantStaleDetails['changed'][number];

/**
 * What a changed product is now: a price, «Oculto», «cambió…» or «ya no
 * está» (a stock item: «ya no está en el inventario», plan P5-3); for a row
 * of an option group that now reaches more products, its reach («ahora se
 * usa en 4 productos»). Read through its row in the fresh proposal
 * (`group`: its entry of the fresh `affected`); a row that left the
 * proposal (it now has that value already) through the value's v1 shape.
 */
function staleValue(
  entry: TStaleEntry,
  row: IAssistantRow | undefined,
  group: IAssistantAffectedGroup | undefined,
  fmt: ICardFormat
): string {
  const { now } = entry;
  if (now === null)
    return fmt.t(
      isStockItemKey(entry.key)
        ? 'assistant.result.staleGoneStock'
        : 'assistant.result.staleGone'
    );
  const grown = row ? grownGroupText(row, group, fmt) : null;
  if (grown) return grown;
  const presenter = row ? presenterOf(row) : presenterOfValue(now);
  return (
    presenter.stale?.(now, row ?? null, fmt) ??
    fmt.t('assistant.result.staleText')
  );
}

/**
 * «Napolitana · Grande»: what changed, as the 409 names it; with its field
 * title when its row in the fresh proposal says its subject alone does not
 * say what changes («Aceite · Costo» next to «ahora $ 4.500», which would
 * read as a price; P5-ADM-E2E review fix).
 */
function staleLabel(
  entry: TStaleEntry,
  row: IAssistantRow | undefined,
  fmt: ICardFormat
): string {
  if (row) {
    const presenter = presenterOf(row);
    if (presenter.subjectNeedsField)
      return rowLabelWithField(row, presenter, fmt);
  }
  return entry.sizeName
    ? `${entry.itemName} · ${entry.sizeName}`
    : entry.itemName;
}

function staleRows(
  changed: IAssistantStaleDetails['changed'],
  rows: IAssistantRow[],
  affected: IAssistantAffectedGroup[],
  fmt: ICardFormat
): IGhCalloutRow[] {
  const rowOf = new Map(rows.map((row) => [row.key, row]));
  const groupOf = affectedGroupByRow(rows, affected);
  const shown =
    changed.length > CALLOUT_ROWS_MAX
      ? changed.slice(0, CALLOUT_ROWS_MAX - 1)
      : changed;
  const lines: IGhCalloutRow[] = shown.map((entry) => ({
    label: clampText(
      staleLabel(entry, rowOf.get(entry.key), fmt),
      ROW_LABEL_MAX
    ),
    value: clampText(
      staleValue(entry, rowOf.get(entry.key), groupOf.get(entry.key), fmt),
      ROW_VALUE_MAX
    ),
  }));
  if (changed.length > shown.length)
    lines.push({
      label: clampText(
        fmt.t('assistant.activity.more', { n: changed.length - shown.length }),
        ROW_LABEL_MAX
      ),
    });
  return lines;
}

export interface IStaleCardInput {
  id: string;
  /** The products that changed since the member saw them (409 `details.changed`). */
  changed: IAssistantStaleDetails['changed'];
  /**
   * The fresh proposal's rows (`details.proposal.rows`); absent or [] when
   * there is none: each change is then read by its value's v1 shape.
   */
  rows?: IAssistantRow[];
  /**
   * The fresh proposal's `affected` (P3): a stale row of an option group
   * that now reaches more products says so; absent or [] when there is none.
   */
  affected?: IAssistantAffectedGroup[];
  /**
   * The fresh proposal's action payload («Revisar de nuevo», «Descartar»);
   * null when it has nothing left to apply (it was discarded).
   */
  payload: IGhPayload | null;
  /**
   * The proposal's kind; absent: a change of the menu. An inventory change
   * (plan P5-3) says the inventory changed, never the menu or its products.
   */
  kind?: TAssistantKind;
}

/**
 * The stale card of each kind: its title, and whether its rows may all be
 * products' (then it speaks of products). An inventory change says the
 * inventory changed, and its rows are never products (plan P5-3).
 */
const STALE_KINDS: Readonly<
  Record<TAssistantKind, { titleKey: string; products: boolean }>
> = {
  'menu.changes': { titleKey: 'assistant.result.staleTitle', products: true },
  'stock.changes': {
    titleKey: 'assistant.result.staleTitleStock',
    products: false,
  },
};

/**
 * «No se aplicó · La carta cambió mientras revisaba.» («El inventario
 * cambió…» for an inventory change): what changed and what it is now;
 * nothing was applied. «Revisar de nuevo» and «Descartar», or, with nothing
 * left to apply, that it is so. Changes of more than products (a category,
 * a menu's order, the inventory) are not called products (plan P2-4).
 */
export function staleCard(
  input: IStaleCardInput,
  fmt: ICardFormat
): IGhCalloutCard {
  const staleKind = ofKind(STALE_KINDS, input.kind);
  // The badge and the title already say nothing was applied and why.
  const changes =
    !staleKind.products ||
    !input.changed.every((entry) => isProductKey(entry.key));
  const textKey = !input.payload
    ? nothingLeftKey(changes, input.kind)
    : changes
    ? 'assistant.result.staleNoteAny'
    : 'assistant.result.staleNote';
  const card: IGhCalloutCard = {
    id: input.id,
    v: 1,
    type: 'callout',
    tone: 'warning',
    badge: {
      text: clampText(fmt.t('assistant.result.notApplied'), LABEL_MAX),
      tone: 'warning',
      icon: 'alertCircle',
    },
    title: clampText(fmt.t(staleKind.titleKey), TITLE_MAX),
    text: clampText(fmt.t(textKey), TEXT_MAX),
  };
  if (input.payload)
    card.actions = [
      refreshAction(input.payload, 'assistant.result.retry', fmt),
      discardAction(input.payload, fmt),
    ];
  const rows = staleRows(
    input.changed,
    input.rows ?? [],
    input.affected ?? [],
    fmt
  );
  if (rows.length) card.rows = rows;
  return card;
}

/**
 * «Esta propuesta venció. ¿La preparo de nuevo?» with «Preparar de nuevo»,
 * when the expired proposal itself cannot be shown again.
 */
export function expiredNoticeCard(
  id: string,
  payload: IGhPayload,
  fmt: ICardFormat
): IGhCalloutCard {
  const card = notice(id, 'neutral', fmt.t('assistant.result.expired'));
  card.actions = [refreshAction(payload, 'assistant.result.prepareAgain', fmt)];
  return card;
}

/** «Se reemplazó por la propuesta nueva.» */
export function supersededCard(id: string, fmt: ICardFormat): IGhCalloutCard {
  return notice(id, 'neutral', fmt.t('assistant.result.superseded'));
}

/** «Propuesta descartada» */
export function discardedCard(id: string, fmt: ICardFormat): IGhCalloutCard {
  return notice(id, 'neutral', fmt.t('assistant.result.discarded'));
}

/** The «nothing left» text of each kind (`nothingLeftKey`). */
const NOTHING_LEFT_KEYS: Readonly<
  Record<TAssistantKind, { products: string; changes: string }>
> = {
  'menu.changes': {
    products: 'assistant.result.nothingLeft',
    changes: 'assistant.result.nothingLeftAny',
  },
  'stock.changes': {
    products: 'assistant.result.nothingLeftStock',
    changes: 'assistant.result.nothingLeftStock',
  },
};

/**
 * «No queda nada por cambiar. Esos productos ya cambiaron…», or «…Eso ya
 * cambió…» when the change is not of products only (`changes`, plan P2-4),
 * «…ya no está en el inventario» for an inventory change (plan P5-3).
 */
function nothingLeftKey(
  changes: boolean,
  kind: TAssistantKind = 'menu.changes'
): string {
  const keys = ofKind(NOTHING_LEFT_KEYS, kind);
  return changes ? keys.changes : keys.products;
}

/**
 * A refreshed proposal with no rows: everything it changed already changed
 * or is gone. `changes`: it changed more than products (its card's payload
 * says so, `proposalPayload`); `kind`: the proposal's (`payloadKind`).
 */
export function nothingLeftCard(
  id: string,
  changes: boolean,
  fmt: ICardFormat,
  kind: TAssistantKind = 'menu.changes'
): IGhCalloutCard {
  return notice(id, 'neutral', fmt.t(nothingLeftKey(changes, kind)));
}

/** «Ya no se puede deshacer este cambio.» */
export function undoWindowCard(id: string, fmt: ICardFormat): IGhCalloutCard {
  return notice(id, 'neutral', fmt.t('assistant.result.undoWindow'));
}

/** «Llegó al límite de cambios de hoy. Podrá seguir mañana.» */
export function quotaCard(id: string, fmt: ICardFormat): IGhCalloutCard {
  return notice(id, 'warning', fmt.t('assistant.result.quota'));
}

/** «Hay una versión nueva. Recargue la página.» with «Recargar». */
export function updateRequiredCard(
  id: string,
  fmt: ICardFormat
): IGhCalloutCard {
  const card = notice(id, 'info', fmt.t('assistant.result.updateRequired'));
  card.actions = [
    {
      id: 'reload',
      kind: 'host',
      label: clampText(fmt.t('assistant.result.reload'), LABEL_MAX),
      icon: 'refresh',
      style: 'outline',
    },
  ];
  return card;
}

/** What is switched off: every write of the assistant, or only the change asked for. */
export type TWritesOff = 'all' | 'op';

/** The writes-off texts of each kind: the member makes the change on its own page. */
const WRITES_OFF_KEYS: Readonly<
  Record<TAssistantKind, Readonly<Record<TWritesOff, string>>>
> = {
  'menu.changes': {
    all: 'assistant.result.writesOff',
    op: 'assistant.result.opOff',
  },
  // From the inventory, never the Menú (plan P5-3).
  'stock.changes': {
    all: 'assistant.result.writesOffStock',
    op: 'assistant.result.opOffStock',
  },
};

/**
 * «…no puede hacer cambios…», or «…no puede hacer este cambio…» for one op;
 * «…desde el menú», or «…desde el inventario» for an inventory change.
 */
export function writesOffText(
  off: TWritesOff,
  fmt: ICardFormat,
  kind: TAssistantKind = 'menu.changes'
): string {
  return fmt.t(ofKind(WRITES_OFF_KEYS, kind)[off]);
}

/** The button of each kind's writes-off card: the page of its changes. */
const WRITES_OFF_ACTIONS: Readonly<
  Record<TAssistantKind, { id: string; labelKey: string }>
> = {
  'menu.changes': { id: 'openMenu', labelKey: 'assistant.result.openMenu' },
  'stock.changes': { id: 'viewStock', labelKey: 'assistant.result.viewStock' },
};

/** The button of a writes-off card: the page of the change's kind. */
function writesOffAction(
  page: string,
  kind: TAssistantKind,
  fmt: ICardFormat
): IGhAction {
  const button = ofKind(WRITES_OFF_ACTIONS, kind);
  return {
    id: button.id,
    kind: 'navigate',
    label: clampText(fmt.t(button.labelKey), LABEL_MAX),
    icon: 'openInNew',
    style: 'outline',
    target: { page },
  };
}

/**
 * «Por ahora el asistente no puede hacer cambios…» (or «…este cambio…»
 * when only its op is off) with «Abrir el Menú», or, for an inventory
 * change, «…desde el inventario» with «Ver el stock» (`page`: that page
 * when this member can open it; none → no button).
 */
export function writesOffCard(
  id: string,
  off: TWritesOff,
  page: string | null,
  fmt: ICardFormat,
  kind: TAssistantKind = 'menu.changes'
): IGhCalloutCard {
  const card = notice(id, 'neutral', writesOffText(off, fmt, kind));
  if (page) card.actions = [writesOffAction(page, kind, fmt)];
  return card;
}
